import { useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CardQuestion, CardActions } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import type { CardDef } from "../types";
import type { DocumentReferenceLink } from "@/lib/types";
import { Plus, X, Link } from "lucide-react";

interface UrlLinksCardProps {
  card: CardDef;
  value: DocumentReferenceLink[];
  onValue: (v: DocumentReferenceLink[]) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  index: number;
}

export function UrlLinksCard({
  card,
  value,
  onValue,
  onNext,
  onBack,
  onSkip,
  index,
}: UrlLinksCardProps) {
  const { _ } = useLingui();
  const [draft, setDraft] = useState("");

  const addLink = () => {
    const url = draft.trim();
    if (!url) return;
    try {
      new URL(url.startsWith("http") ? url : `https://${url}`);
    } catch {
      return;
    }
    onValue([...value, { url: url.startsWith("http") ? url : `https://${url}` }]);
    setDraft("");
  };

  const removeLink = (idx: number) => {
    onValue(value.filter((_, i) => i !== idx));
  };

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: onNext,
      onCancel: onBack,
      onSkip,
    }),
    [onNext, onBack, onSkip],
  );

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      <div className="max-w-lg space-y-3">
        {/* Existing links */}
        {value.map((link, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-200"
          >
            <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="flex-1 text-sm text-[#166B93] truncate">{link.url}</span>
            <button
              type="button"
              onClick={() => removeLink(i)}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* Input row */}
        <div className="flex items-center gap-3">
          <input
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addLink();
              }
            }}
            placeholder={_(msg`https://drive.google.com/...`)}
            className="flex-1 w-full bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 py-3.5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)]"
          />
          <button
            type="button"
            onClick={addLink}
            className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#166B93] text-white flex items-center justify-center hover:bg-[#105373] transition-colors shadow-md"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </div>

      <CardActions
        onNext={onNext}
        onBack={onBack}
        onSkip={onSkip}
      />
    </div>
  );
}
